import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createContext, useContext, useState, ReactNode } from 'react'
import axios from 'axios'
import { setAccessToken } from '../utils/assetUrl'

// Types
export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  avatarUrl?: string
  role: string
  area?: { id: string; name: string }
  companyId: string
  isActive: boolean
}

export interface TwoFactorChallenge {
  tempToken: string
  user: { id: string; email: string; firstName: string; lastName: string }
}

export interface LoginResult {
  twoFactorRequired: boolean
  challenge?: TwoFactorChallenge
  access_token?: string
  refresh_token?: string
  user?: User
  twoFactorUsedBackup?: boolean
}

export interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<LoginResult>
  verifyTwoFactor: (tempToken: string, code: string) => Promise<LoginResult>
  logout: () => void
  refreshToken: () => Promise<void>
  clearError: () => void
  updateUser: (updates: Partial<User>) => void
}

const API_URL = import.meta.env.VITE_API_URL;

// Bandera a nivel de módulo para evitar múltiples redirects en paralelo
// cuando varias peticiones fallan con 401 al mismo tiempo.
let sessionExpiredHandled = false;
// API instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para agregar token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Store de Zustand
const createStore = () => create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/login', { email, password })
          const data = response.data

          if (data.twoFactorRequired) {
            set({ isLoading: false, error: null })
            return {
              twoFactorRequired: true,
              challenge: { tempToken: data.tempToken, user: data.user },
            }
          }

          const { user, access_token, refresh_token } = data
          localStorage.setItem('auth_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)
          setAccessToken(access_token)
          sessionExpiredHandled = false
          sessionStorage.removeItem('auth:expired_redirects')

          set({
            user,
            token: access_token,
            isAuthenticated: true,
            isLoading: false,
          })
          return {
            twoFactorRequired: false,
            access_token,
            refresh_token,
            user,
            twoFactorUsedBackup: data.twoFactorUsedBackup,
          }
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al iniciar sesión',
            isLoading: false,
          })
          throw error
        }
      },

      verifyTwoFactor: async (tempToken: string, code: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/2fa/authenticate', { tempToken, code })
          const { access_token, refresh_token, user, twoFactorUsedBackup } = response.data

          localStorage.setItem('auth_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)
          setAccessToken(access_token)
          sessionExpiredHandled = false
          sessionStorage.removeItem('auth:expired_redirects')

          set({
            user,
            token: access_token,
            isAuthenticated: true,
            isLoading: false,
          })
          return {
            twoFactorRequired: false,
            access_token,
            refresh_token,
            user,
            twoFactorUsedBackup,
          }
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Código 2FA inválido',
            isLoading: false,
          })
          throw error
        }
      },
      logout: () => {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('refresh_token')
        setAccessToken(null)
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        })
      },

      refreshToken: async () => {
        const refresh_token = localStorage.getItem('refresh_token')
        if (!refresh_token) {
          // No hay refresh_token disponible, no hacer nada
          // (el backend puede no haber sido deployado con soporte para refresh)
          return
        }

        try {
          const response = await api.post('/auth/refresh-token', { refresh_token })
          const { access_token } = response.data
          localStorage.setItem('auth_token', access_token)
          setAccessToken(access_token)
          set({ token: access_token })
          sessionStorage.removeItem('auth:expired_redirects')
        } catch (err) {
          // El refresh falló. Si el backend respondió 401, el refresh_token también expiró.
          // No desautenticamos aquí automáticamente porque el useApi se encargará.
          console.warn('[auth] refreshToken falló:', err)
        }
      },

      clearError: () => set({ error: null }),

      updateUser: (updates: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }))
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        return (state) => {
          if (state?.token) {
            localStorage.setItem('auth_token', state.token)
            setAccessToken(state.token)
          }
        }
      },
    }
  )
)

// Context para usar en componentes
const AuthContext = createContext<ReturnType<typeof createStore> | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => createStore())
  return <AuthContext.Provider value={store}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context()
}

// Hook personalizado para peticiones API
export function useApi() {
  const { token, refreshToken, logout } = useAuth()

  const handleSessionExpired = () => {
    if (sessionExpiredHandled) return
    sessionExpiredHandled = true
    console.warn('[auth] Sesión expirada o token inválido. Cerrando sesión.')
    // Guard contra loop infinito: contar redirects en sessionStorage.
    // Si en esta pestaña ya redirigimos 3 veces por sesión expirada,
    // mostrar pantalla de error en vez de seguir redirigiendo.
    const redirectKey = 'auth:expired_redirects'
    const prevCount = parseInt(sessionStorage.getItem(redirectKey) || '0', 10)
    const newCount = prevCount + 1
    sessionStorage.setItem(redirectKey, String(newCount))
    if (newCount >= 3) {
      console.error('[auth] Loop de sesión expirada detectado. Mostrando pantalla de error.')
      sessionStorage.removeItem(redirectKey)
      window.location.replace('/login?loop=1')
      return
    }
    // Limpiar TODO el storage relacionado con auth
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('auth-storage')
    setAccessToken(null)
    logout()
    const returnTo = window.location.pathname + window.location.search
    setTimeout(() => {
      window.location.replace(`/login?expired=1&returnTo=${encodeURIComponent(returnTo)}`)
    }, 100)
  }

  // request hace un fetch directo. Para reintento después del refresh,
  // hacemos una función auto-recursiva que LEE el token de localStorage (no de la closure)
  // para garantizar que use el token nuevo después del refresh.
  const doFetch = async <T,>(url: string, options: RequestInit, currentToken: string | null): Promise<Response> => {
    return fetch(`${API_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}),
        ...options.headers,
      },
    })
  }

  const request = async <T extends unknown>(url: string, options: RequestInit = {}): Promise<T> => {
    const response = await doFetch<T>(url, options, token)

    if (response.status === 401) {
      // Intentar refresh UNA sola vez
      const refresh_token = localStorage.getItem('refresh_token')
      if (!refresh_token) {
        handleSessionExpired()
        throw new Error('401: Sesión expirada')
      }

      try {
        await refreshToken()
        const newToken = localStorage.getItem('auth_token')
        if (!newToken) {
          // refresh no actualizó el token → falló silenciosamente
          handleSessionExpired()
          throw new Error('401: Sesión expirada')
        }
        // Reintentar con el nuevo token (leído de localStorage, no de la closure)
        const retryResponse = await doFetch<T>(url, options, newToken)
        if (retryResponse.status === 401) {
          // El nuevo token también es inválido
          handleSessionExpired()
          throw new Error('401: Sesión expirada')
        }
        if (!retryResponse.ok) {
          let errorMessage = `Error ${retryResponse.status}: ${retryResponse.statusText}`
          try {
            const errorData = await retryResponse.json()
            errorMessage = errorData.message || errorMessage
          } catch {
            // ignore
          }
          throw new Error(errorMessage)
        }
        return retryResponse.json()
      } catch (err) {
        if (err instanceof Error && err.message.startsWith('401:')) {
          // Ya manejamos arriba
          throw err
        }
        // Error de red en el refresh
        handleSessionExpired()
        throw new Error('401: Sesión expirada')
      }
    }

    if (!response.ok) {
      let errorMessage = `Error ${response.status}: ${response.statusText}`
      try {
        const errorData = await response.json()
        errorMessage = errorData.message || errorMessage
      } catch {
        // Si no es JSON, usar el statusText
      }
      throw new Error(errorMessage)
    }

    return response.json()
  }

  return {
    get: <T,>(url: string) => request<T>(url, { method: 'GET' }),
    post: <T,>(url: string, data: any) => request<T>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    put: <T,>(url: string, data: any) => request<T>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    patch: <T,>(url: string, data: any) => request<T>(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: <T,>(url: string) => request<T>(url, { method: 'DELETE' }),
  }
}
